import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBookmarks, deleteBookmark, addBookmark } from '../store/bookmarkSlice';
import { ReactComponent as BookMark } from '../assets/bookmark.svg';
import { useTranslation } from "react-i18next";

const BookmarkItem = ({ bookmark, onDelete, onNavigate, columnId }) => (
  <li className="flex justify-between items-center py-1 px-2 rounded hover:bg-gray-100">
    <button
      onClick={() => onNavigate(columnId, bookmark.book, bookmark.chapter, bookmark.version)}
      className="text-blue-500 hover:underline text-sm flex items-center gap-2"
    >
      📖 {bookmark.book} {bookmark.chapter} ({bookmark.version})
    </button>
    <button
      onClick={() => onDelete(bookmark.id)}
      className="text-red-500 hover:text-red-700 text-sm"
    >
      x
    </button>
  </li>
);

const BookmarkManager = ({ columnId, column, onNavigate }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.session.user);
  const { bookmarks, loading, error } = useSelector((state) => state.bookmarks);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasFetchedBookmarks, setHasFetchedBookmarks] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const { t } = useTranslation();
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle menu and fetch bookmarks if necessary
  const handleToggleMenu = useCallback(() => {
    if (!isMenuOpen && user && !hasFetchedBookmarks) {
      dispatch(fetchBookmarks());
      setHasFetchedBookmarks(true); // Prevent repeated fetches
    }
    setIsMenuOpen((prev) => !prev);
  }, [isMenuOpen, user, hasFetchedBookmarks, dispatch]);

  // Add a new bookmark
  const handleAddBookmark = useCallback(async () => {
    const { version, book, chapter } = column;

    // Check for duplicate bookmarks
    const isDuplicate = bookmarks.some(
      (bookmark) =>
        bookmark.version === version &&
        bookmark.book === book &&
        bookmark.chapter === chapter
    );

    if (isDuplicate) {
      alert('This bookmark already exists.');
      return;
    }

    try {
      await dispatch(
        addBookmark({ book, chapter, version, category: 'General' })
      ).unwrap();
      dispatch(fetchBookmarks()); // Refetch bookmarks after adding
    } catch (error) {
      console.error('Failed to add bookmark:', error);
    }
  }, [column, bookmarks, dispatch]);

  // Delete a bookmark
  const handleDeleteBookmark = useCallback(
    async (bookmarkId) => {
      try {
        await dispatch(deleteBookmark(bookmarkId)).unwrap();
        dispatch(fetchBookmarks()); // Refetch bookmarks after deletion
      } catch (err) {
        console.error('Failed to delete bookmark:', err);
      }
    },
    [dispatch]
  );

  // Memoize rendered bookmarks
  const renderedBookmarks = useMemo(() => {
    if (!user || loading || error) return null;

    return bookmarks.map((bookmark) => (
      <BookmarkItem
        key={bookmark.id}
        bookmark={bookmark}
        onDelete={handleDeleteBookmark}
        onNavigate={onNavigate}
        columnId={columnId}
      />
    ));
  }, [bookmarks, user, loading, error, handleDeleteBookmark, onNavigate, columnId]);

  return (
    <div className="relative bookmark w-full">
      {/* Toggle Button */}
      <button
        ref={buttonRef}
        onClick={handleToggleMenu}
        className="text-black px-1 py-1 rounded-xl bg-white h-10 w-full flex items-center justify-center shadow-[-2px_-2px_4px_var(--outer-shadow-light),2px_2px_4px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_2px_var(--outer-shadow-light),1px_1px_2px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all"
      >
        <BookMark className="h-6 w-6 mr-2" />
        {t('bookmark')}
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div
          ref={menuRef}
          className="absolute top-12 bg-white border border-gray-300 shadow-lg rounded-lg z-50 p-1"
          style={{
            left: '-85px',
            minWidth: '200px',
            maxHeight: '300px',
            overflowY: 'auto',
            animation: 'fadeIn 0.3s ease-in-out',
          }}
        >
          <h3 className="text-lg font-semibold mb-2 border-b pb-2">Bookmarks</h3>
          {loading && <p className="text-sm text-gray-500">Loading bookmarks...</p>}
          {error && user && (
            <p className="text-sm text-red-500">Failed to load bookmarks: {error}</p>
          )}
          {!loading && user && !error && bookmarks.length === 0 && (
            <p className="text-sm text-gray-500">No bookmarks available.</p>
          )}
          {!user && (
            <p className="text-sm text-gray-500 h-10 mt-5">Log in to manage bookmarks.</p>
          )}

          {/* Render Bookmarks */}
          <ul>{renderedBookmarks}</ul>

          {/* Add Bookmark Button */}
          {user && (
            <button
              onClick={handleAddBookmark}
              className="block w-full mt-4 text-center py-2 text-white bg-darkBlue rounded hover:bg-green-600 text-sm font-semibold"
            >
              + Add Bookmark
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BookmarkManager;
